/**
 * Lots grid of an inventory transfer — EditableDataGrid consumer (wave W4b).
 *
 *   InventoryTransferItemsSection    ── owns the store handle (1b bridge)
 *     └─ handle.Provider ────────────── rows live HERE, not in useState
 *          ├─ TransferScanner  ───────  reads the same rows
 *          └─ InventoryTransferGrid ──  EditableDataGrid + InventoryTransferPalletTagModal
 *
 * WHY the Provider is one level up: `transferRows` used to be
 * `useState(inventoryTransfer.lotTransferItems)` in the Cell and was shared
 * with the barcode scanner. One store, one owner — so the items section owns it and this
 * component takes `handleRequest` / `removeRow` instead of `rows` / `setRows`.
 *
 * READ-ONLY  canEdit — the lots policy (`useInventoryTransferItemPolicy`) — is passed
 *            straight to the core (ruling B4); `columnLock` adds each locked
 *            column's tooltip. The core
 *            derives isCellEditable, single-click entry, canAdd and
 *            disabledColumns.actions from it; this file re-implements none of
 *            them. `canEdit` here also feeds the pallet-tag cell, which is an
 *            affordance inside a NON-editable column and therefore not covered
 *            by cell editability.
 *
 * ROW IDENTITY  every id crossing into a mutation is the STORE rowId, so it is
 *               mapped back through `getLocalRow(rowId).id` before it reaches
 *               the server (ruling B1).
 */

import { Box } from '@mui/material';
import {
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowId,
  isAutogeneratedRow,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import type { InventoryTransferItemField } from '@shared/core/policy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BoldHeader from 'src/components/atoms/grid/BoldHeader';
import Link from 'src/components/atoms/Link';
import Skeleton from 'src/components/atoms/Skeleton';
import SkeletonDataGrid from 'src/components/containers/skeleton/SkeletonDataGrid';
import { AgText } from 'src/components/ui';
import type { RequestQueue } from 'src/hooks/useQueuedRequests';
import useResponsiveCellEditValue from 'src/hooks/useResponsiveCellEditValue';
import useSystemUnitRounding from 'src/hooks/useSystemUnitRounding';
import type { GridRow, GridRowRemoveEvent, GridValidationContext } from 'src/lib/gridRows.store';
import { EditableDataGrid, GridColObj, useEditableGridRows } from 'src/modules/grids';
import EditLotCell from 'src/modules/inventory/containers/EditLotCell';
import { appLinks } from 'src/routing/appLinks';
import columnActions from 'src/utils/grid/column/columnActions';
import { gridRowHeight } from 'src/utils/grid/gridStyles';
import { z } from 'zod';

import { useCreateLotTransferItem } from './hooks/useCreateLotTransferItem';
import { useDeleteLotTransferItem } from './hooks/useDeleteLotTransferItem';
import { useFindTransferDestinationLot } from './hooks/useFindTransferDestinationLot';
import { useSetLotTransferItemTags } from './hooks/useSetLotTransferItemTags';
import { useUpdateLotTransferItem } from './hooks/useUpdateLotTransferItem';
import InventoryTransferPalletTagModal, { type EditingTagsForRow } from './InventoryTransferPalletTagModal';
import { InventoryTransferPalletTagsCell } from './InventoryTransferPalletTagsCell';

/* ------------------------ ROW / INPUT SHAPES ------------------------ */

export type LotTransferItemPalletTag = {
  palletTag: { id: number; tagNumber?: string; cartonQuantity?: number | null };
  quantity?: number | null;
  deletedAt?: string | null;
};

export type LotTransferItemLot = {
  id?: number;
  slug?: string;
  warehouseId?: number | null;
  /** Present only while the Algolia edit cell holds an unsaved pick. */
  quantity?: number | null;
  metrics?: { unitsAvailable?: number | null } | null;
  product?: { description?: string | null } | string | null;
  transferredChildren?: ({ id?: number; slug?: string; warehouseId?: number | null } | null)[] | null;
};

/** GraphQL hands these arrays back with nullable elements; the row type has to say so. */
export type LotTransferItemPalletTags = (LotTransferItemPalletTag | null)[];

export type LotTransferItemRow = {
  id?: number;
  unitsTransferred?: number | null;
  lot?: LotTransferItemLot | null;
  /** Local-only fold used by the scanner and the tags modal; wins over the server field. */
  palletTags?: LotTransferItemPalletTags | null;
  palletTagsOnTransferItem?: LotTransferItemPalletTags | null;
  /** The lot the ship added this row's units to; null until the transfer ships. */
  destinationLot?: { id: number; slug: string; warehouseId?: number | null } | null;
};

export type LotTransferItemInput = { lotId?: number; unitsTransferred?: number | null };

type LotTransferItemRequestQueue = RequestQueue<
  LotTransferItemRow,
  LotTransferItemInput,
  'create' | 'update' | 'delete'
>;

/**
 * ROW-DEPENDENT model: the capacity ceiling is the row's own lot, so the schema cannot be hoisted.
 * `row.lot?.quantity` — the optional chain is required: `createFreshRow` mints `{ lot: null }`, and the store resolves this model with `safeParse`, which does NOT catch a TypeError thrown inside a `.refine`.
 * A shipped row (it has a `destinationLot`) already moved its units out of the lot, so the ceiling no longer applies.
 */
export function lotTransferItemValidationModel<T>({ row }: GridValidationContext<T>): z.ZodType<T> {
  const { lot, destinationLot } = row as GridRow<LotTransferItemRow>;
  const isShipped = destinationLot != null;

  const schema = z.object({
    unitsTransferred: z
      .number({ invalid_type_error: 'Must transfer more than 0 units' })
      .gt(0, { message: 'Must transfer more than 0 units' })
      .refine((value) => isShipped || (lot?.quantity ?? lot?.metrics?.unitsAvailable ?? 0) >= value, {
        message: `Lot does not have enough units to transfer`,
      }),
    lot: z.object(
      {
        id: z.number().optional(),
        slug: z.string().optional(),
        warehouseId: z.number().optional(),
        quantity: z.number().optional(),
        metrics: z.object({ unitsAvailable: z.number().optional() }).optional(),
        transferredChildren: z
          .array(
            z
              .object({ id: z.number().optional(), slug: z.string().optional(), warehouseId: z.number().optional() })
              .optional()
          )
          .optional(),
      },
      { invalid_type_error: 'Lot is required', required_error: 'Lot is required' }
    ),
  });

  // The store only reads `.success` / `.error` off this model, and the same
  // model is resolved for two row types (the store's `R` and the grid's
  // `Record<string, unknown>`). One narrowing hop here keeps every call site
  // cast-free.
  return schema as unknown as z.ZodType<T>;
}

/** `createRow` -> `createFreshRow`. Same body; a draft starts with no lot. */
export const createFreshLotTransferItemRow = (): LotTransferItemRow => ({ lot: null, unitsTransferred: null });

/** `rowToInput` reads the CHANGES object. The store diffs with a deep `isEqual`, so an untouched lot is not in `changes` and `lotId` drops out of the payload — same server outcome, fewer variables on the wire. */
export const lotTransferItemRowToInput = (changes: Partial<GridRow<LotTransferItemRow>>): LotTransferItemInput => ({
  lotId: changes?.lot?.id,
  unitsTransferred: changes?.unitsTransferred,
});

/** The pallet-tag modal owns its row while open — return the row to mark the save HANDLED (no `edit()`, so no mutation). */
export const holdSaveForTagsModal = <Row extends object>(
  editingTagsForRow: EditingTagsForRow,
  row: GridRow<Row>
): GridRow<Row> | undefined => (editingTagsForRow?.id === row.rowId ? row : undefined);

/** Gating stubs the store still asks for; the real gate is `canEdit` in the core. */
export const LOT_TRANSFER_ITEM_CAPABILITIES = {
  canAddEntity: () => true,
  canEditEntity: () => true,
  canDeleteEntity: () => true,
};

/**
 * `BoldHeader` is typed against MUI's default row model, and the two directions
 * are NOT bidirectionally assignable — `GridStateColDef` carries contravariant
 * callbacks, so neither `BoldHeader` nor a widened wrapper satisfies a column
 * typed on `GridRow<LotTransferItemRow>`. One narrowing hop here, where the only
 * fields actually read are `colDef.description` and `colDef.headerName`.
 */
const renderBoldHeader = (params: GridColumnHeaderParams<GridRow<LotTransferItemRow>>) => (
  <BoldHeader {...(params as unknown as GridColumnHeaderParams)} />
);

const LotTrackingLink = ({ slug, warehouseId }: { slug: string; warehouseId: number }) => {
  return <Link {...appLinks.viewLotTracking({ slug, warehouseId })} label={slug} />;
};

/** A draft row the transfer children can't answer: the lot holding this number at the destination, or a new one. */
const ExpectedDestinationLot = ({ slug, warehouseId }: { slug: string; warehouseId: number }) => {
  const { destinationLot, isResolved } = useFindTransferDestinationLot({ slug, warehouseId });

  if (destinationLot?.warehouseId != null) {
    return <LotTrackingLink slug={destinationLot.slug} warehouseId={destinationLot.warehouseId} />;
  }

  if (!isResolved) {
    return null;
  }

  return (
    <AgText size="14" color="secondary" className="italic">
      Lot will be created on transfer
    </AgText>
  );
};

const QuantityAvailableCell = ({ row, id, api }: GridRenderCellParams) => {
  const editLot = useResponsiveCellEditValue({ fieldToSelect: 'lot', id });
  const editUnitsTransferred = useResponsiveCellEditValue({ fieldToSelect: 'unitsTransferred', id });
  const rowMode = api.getRowMode(id);
  const isEditing = rowMode === 'edit';
  const quantityAvailable = isEditing ? (editLot?.quantity ?? 0) : (row?.lot?.metrics?.unitsAvailable ?? 0);

  if (isAutogeneratedRow(row) || !(isEditing ? editLot : row.lot)) {
    return null;
  }

  return quantityAvailable - (editUnitsTransferred ?? 0);
};

/** `usePolicy(inventoryTransferItemPolicy).column` — the locked cell and its tooltip, from one decision. */
type LotTransferColumnLock = (field: InventoryTransferItemField) => {
  isCellEditable: (row: unknown) => boolean;
  lockReason: (row: unknown) => string | null;
};

type LotTransferColumnArgs = {
  handleDeleteRow?: (rowId: GridRowId) => void;
  handleClearRow?: (rowId: GridRowId) => void;
  fromWarehouseId?: number;
  toWarehouseId?: number;
  handleOpenTagsModal?: (rowId: GridRowId) => void;
  canEdit?: boolean;
  columnLock?: LotTransferColumnLock;
  unitRounding?: (value: number) => number;
  lotsRef?: React.MutableRefObject<Record<string, number>>;
};

export const lotTransferColumns = ({
  handleDeleteRow,
  handleClearRow,
  fromWarehouseId,
  toWarehouseId,
  handleOpenTagsModal,
  canEdit,
  columnLock,
  unitRounding,
  lotsRef,
}: LotTransferColumnArgs = {}): GridColObj<GridRow<LotTransferItemRow>> => {
  return {
    lot: {
      headerName: 'Origin Lot',
      editable: true,
      ...columnLock?.('lot'),
      flex: 1,
      type: 'string',
      headerAlign: 'left',
      renderCell: ({ row }) => {
        const lot = row.lot;

        if (isAutogeneratedRow(row) || !lot?.slug || lot.warehouseId == null) {
          return undefined;
        }

        return <LotTrackingLink slug={lot.slug} warehouseId={lot.warehouseId} />;
      },
      renderEditCell: (params) => (
        <EditLotCell
          warehouseId={fromWarehouseId}
          {...params}
          getLotTotals={() => lotsRef?.current ?? {}}
          disableClearable
        />
      ),
      align: 'left',
      renderHeader: renderBoldHeader,
    },
    quantityAvailable: {
      headerName: 'Quantity Available',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      renderCell: (params) => <QuantityAvailableCell {...params} />,
      align: 'left',
      renderHeader: renderBoldHeader,
    },
    product: {
      headerName: 'Product',
      flex: 1,
      editable: true, // not really editable, but we wanna display the select lot product
      // `editable: true` above exists ONLY so row edit keeps rendering the lot's
      isCellEditable: () => false,
      type: 'string',
      headerAlign: 'left',
      // MAYBE: Allow this to behave as a product filter for lots,
      // Unsure how to do this in a user friendly way, since we'd want the lot to still be editable.
      // So if the user selects a lot, and that updates the product -
      // they'd have to clear the product to update the lot?
      valueGetter: (_value, row) => {
        const product = row.lot?.product;

        return typeof product === 'string' ? product : product?.description;
      },
      renderEditCell: ({ row }) => {
        const product = row.lot?.product;
        const isString = typeof product === 'string';

        return (
          <AgText size="14" className="px-1">
            {isString ? product : product?.description}
          </AgText>
        );
      },
      align: 'left',
      renderHeader: renderBoldHeader,
    },
    unitsTransferred: {
      headerName: 'Units Transferred',
      editable: true,
      ...columnLock?.('unitsTransferred'),
      type: 'number',
      minWidth: 120,
      flex: 1,
      valueSetter: (value: number, row) => ({ ...row, unitsTransferred: unitRounding ? unitRounding(value) : value }),
      renderHeader: renderBoldHeader,
    },
    palletTags: {
      headerName: 'Tags',
      flex: 1,
      sortable: false,
      description: '',
      editable: false,
      renderCell: (params) => (
        <InventoryTransferPalletTagsCell
          handleOpenTagsModal={handleOpenTagsModal}
          canEdit={canEdit}
          getRowPalletTags={(row) => row?.palletTags ?? row?.palletTagsOnTransferItem}
          {...params}
        />
      ),
      renderEditCell: (params) => (
        <Box sx={{ px: '10px', width: '100%' }}>
          <InventoryTransferPalletTagsCell
            handleOpenTagsModal={handleOpenTagsModal}
            canEdit={canEdit}
            isEditing
            getRowPalletTags={(row) => row?.palletTags ?? row?.palletTagsOnTransferItem}
            {...params}
          />
        </Box>
      ),
    },
    transferredLot: {
      headerName: 'Destination Lot',
      editable: false,
      flex: 1,
      type: 'string',
      headerAlign: 'left',
      // Shipped: the lot the ship recorded. Before that, a transfer child at the destination already holds the
      // origin's number there, so it is the lot the ship adds to.
      valueGetter: (_value, row) =>
        row.destinationLot ?? row.lot?.transferredChildren?.find((child) => child?.warehouseId === toWarehouseId),
      renderCell: ({ value, row }) => {
        // A row with no lot picked has no destination lot to name yet.
        if (isAutogeneratedRow(row) || !row.lot?.slug) {
          return undefined;
        }

        if (value?.slug && value.warehouseId != null) {
          return <LotTrackingLink slug={value.slug} warehouseId={value.warehouseId} />;
        }

        // No Ship To yet: nowhere to look.
        if (toWarehouseId == null) {
          return undefined;
        }

        // Not a child: the ship still merges into any lot with this number there (e.g. the root it came from).
        return <ExpectedDestinationLot slug={row.lot.slug} warehouseId={toWarehouseId} />;
      },

      align: 'left',
      renderHeader: renderBoldHeader,
    },
    actions: {
      type: 'actions',
      width: 10,
      maxWidth: 10,
      align: 'left',
      getActions: columnActions(
        ({ id }) => ({
          deleteRow: () => {
            handleDeleteRow?.(id);
          },
          clearRow: () => {
            handleClearRow?.(id);
          },
        }),
        { showOnHover: true }
      ),
    },
  };
};

type InventoryTransferGridProps = {
  canEdit: boolean;
  /** Per-column lock + tooltip from the lots policy; `canEdit` stays the grid-wide gate. */
  columnLock?: LotTransferColumnLock;
  /** From the Cell's `useLocalEditableGridStore` handle — the store owns the rows. */
  handleRequest: LotTransferItemRequestQueue;
  /** `handle.remove`; returns undefined for drafts (removed locally, no server call). */
  removeRow: (rowId: GridRowId) => GridRowRemoveEvent | undefined;
  fromWarehouseId?: number;
  toWarehouseId?: number;
  inventoryTransferId: number;
  rowCountLabel?: string;
};

function requireData<Data>(data: Data | null | undefined, operationName: string): Data {
  if (data == null) throw new Error(`${operationName} returned no data`);
  return data;
}

export const INVENTORY_TRANSFER_GRID_ID = 'inventory-transfer-grid';
const INVENTORY_TRANSFER_GRID_KEY = 'inventory.transfer.items';

export const InventoryTransferGrid = ({
  canEdit,
  columnLock,
  handleRequest,
  removeRow,
  inventoryTransferId,
  fromWarehouseId,
  toWarehouseId,
  rowCountLabel,
}: InventoryTransferGridProps) => {
  const apiRef = useGridApiRef();

  const [editingTagsForRow, setEditingTagsForRow] = useState<EditingTagsForRow>();

  // `getLocalRows()` builds a NEW array on every call, and `useStoreState` hands
  const localRowsById = useEditableGridRows((store) => store.localRows);
  const localRows = useMemo(
    () => Object.values(localRowsById).filter((row) => !row.isDeleted),
    [localRowsById]
  ) as GridRow<LotTransferItemRow>[];
  const getLocalRow = useEditableGridRows((store) => store.getLocalRow);
  const setLocalRow = useEditableGridRows((store) => store.setLocalRow);

  const { createLotTransferItem } = useCreateLotTransferItem();
  const { remove } = useDeleteLotTransferItem();
  const { update } = useUpdateLotTransferItem();
  const { setLotTransferItemTags } = useSetLotTransferItemTags();

  /**
   * Create/edit return the ACCEPTED ROW; the bridge folds it through
   * `reconcileEdit`. The old `onCompleted` handlers that hand-folded the server
   * row back into local state are gone — double-applying would fight the store's
   * patch bookkeeping.
   */
  const handleCreate = useCallback(
    ({ input }: { input: LotTransferItemInput }): Promise<LotTransferItemRow> => {
      const { lotId } = input;
      if (lotId === undefined) return Promise.reject(new Error('CreateLotTransferItem needs a lot'));

      return createLotTransferItem({ variables: { input: { ...input, lotId, inventoryTransferId } } }).then(
        ({ data }) => requireData(data, 'CreateLotTransferItem').createLotTransferItem
      );
    },
    [createLotTransferItem, inventoryTransferId]
  );

  const handleEdit = useCallback(
    ({ id, input }: { id: number; input: LotTransferItemInput }): Promise<LotTransferItemRow> => update({ id, input }),
    [update]
  );

  const handleDelete = useCallback(
    (id: number): Promise<LotTransferItemRow> =>
      remove(id).then(({ data }) => requireData(data, 'DeleteLotTransferItem').deleteLotTransferItem),
    [remove]
  );

  const handleDeleteRow = useCallback(
    (rowId: GridRowId) => {
      const stateMutation = removeRow(rowId);

      // A draft has no server row: `removeRow` already dropped it locally.
      if (!stateMutation) {
        return;
      }

      handleRequest(rowId, {
        event: handleDelete,
        eventType: 'delete',
        unpackPayload: stateMutation.payload,
        resolve: stateMutation.resolve,
        reject: stateMutation.reject,
      });
    },
    [handleDelete, handleRequest, removeRow]
  );

  /** The "Clear" menu item — local removal only, never a server call. */
  const handleClearRow = useCallback(
    (rowId: GridRowId) => {
      removeRow(rowId)?.resolve();
    },
    [removeRow]
  );

  const handleOpenTagsModal = useCallback((rowId: GridRowId) => {
    setEditingTagsForRow({ id: rowId });
  }, []);

  const handleCloseTagsModal = useCallback(() => {
    setEditingTagsForRow(undefined);
  }, []);

  const saveChanges = useCallback(
    (row: GridRow<LotTransferItemRow>) => holdSaveForTagsModal(editingTagsForRow, row),
    [editingTagsForRow]
  );

  const unitRounding = useSystemUnitRounding();

  const lotsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    lotsRef.current = Object.fromEntries(
      localRows
        .filter((row) => Boolean(row.lot?.slug))
        .map((row) => [row.lot?.slug as string, row.unitsTransferred ?? 0])
    );
  }, [localRows]);

  const columns = useMemo(
    () =>
      lotTransferColumns({
        handleDeleteRow,
        handleClearRow,
        fromWarehouseId,
        toWarehouseId,
        handleOpenTagsModal,
        canEdit,
        columnLock,
        unitRounding,
        lotsRef,
      }),
    [
      handleDeleteRow,
      handleClearRow,
      fromWarehouseId,
      toWarehouseId,
      canEdit,
      columnLock,
      handleOpenTagsModal,
      unitRounding,
    ]
  );

  const handleSaveTransferredTags = useCallback(
    async (
      editingRow: EditingTagsForRow,
      palletTags: { palletTag: { id: number; tagNumber: string }; quantity: number }[]
    ) => {
      if (!editingRow) {
        return;
      }

      const api = apiRef.current;
      if (!api) return;

      const rowId = editingRow.id;
      const isEditing = api.getRowMode(rowId) === 'edit';
      // ruling B1: the modal addresses rows by store rowId; the server wants the DB id.
      const remoteId = getLocalRow(rowId)?.id;

      if (isEditing) {
        const rowNode = api.getRowNode(rowId);

        if (!rowNode) {
          return;
        }

        api.setEditCellValue({ id: rowId, field: 'palletTags', value: palletTags });

        handleCloseTagsModal();
      }

      // setPalletTagsOnTransferItem only works on existing transfer items
      if (remoteId === undefined) {
        handleCloseTagsModal();
        return;
      }

      const { data } = await setLotTransferItemTags({
        variables: {
          id: remoteId,
          // strip tagNumber from the input
          input: palletTags.map(({ palletTag, quantity }) => ({ palletTagId: palletTag.id, quantity })),
        },
      });

      const palletTagsOnTransferItem: LotTransferItemPalletTags = requireData(
        data,
        'SetLotTransferItemTags'
      ).setPalletTagsOnTransferItem.filter((tagOnItem) => !tagOnItem?.deletedAt);

      // A LOCAL fold, never `edit()` — routing it through the save path would
      // enqueue a spurious UpdateLotTransferItem.
      const current = getLocalRow(rowId) as GridRow<LotTransferItemRow> | undefined;

      if (current) {
        const folded: GridRow<LotTransferItemRow> = { ...current, palletTagsOnTransferItem };

        setLocalRow(folded);
      }

      handleCloseTagsModal();
    },
    [apiRef, getLocalRow, handleCloseTagsModal, setLocalRow, setLotTransferItemTags]
  );

  return (
    <>
      <EditableDataGrid<LotTransferItemRow, LotTransferItemInput>
        columns={columns}
        gridKey={INVENTORY_TRANSFER_GRID_KEY}
        gridId="transfer-items"
        rowCountLabel={rowCountLabel}
        apiRef={apiRef}
        storageKey={INVENTORY_TRANSFER_GRID_ID}
        pinnedColumns={{ right: ['actions'] }}
        initialState={{ density: 'compact', aggregation: { model: { unitsTransferred: 'sum' } } }}
        // EditableDataGrid's own default is 48 × densityFactor, not the contract's 44.
        getRowHeight={gridRowHeight}
        surface="bare"
        validationModel={lotTransferItemValidationModel}
        canEdit={canEdit}
        newRowLabel="Enter New Lot"
        rowToInput={lotTransferItemRowToInput}
        createFreshRow={createFreshLotTransferItemRow}
        onCreate={handleCreate}
        onEdit={handleEdit}
        handleRequest={handleRequest}
        saveChanges={saveChanges}
        onProcessRowUpdateError={(error) => {
          console.error(error);
        }}
      />
      <InventoryTransferPalletTagModal
        handleCloseTagsModal={handleCloseTagsModal}
        handleSaveTransferredTags={handleSaveTransferredTags}
        editingTagsForRow={editingTagsForRow}
        rows={localRows}
        apiRef={apiRef}
        getRowPalletTags={(row) => row?.palletTags ?? row?.palletTagsOnTransferItem}
        canEdit={canEdit}
      />
    </>
  );
};

export const ProductsGridSkeleton = () => {
  const columns = useMemo(() => lotTransferColumns(), []);

  return (
    <>
      <Skeleton>
        <AgText size="h6" className="pl-1 pb-2">
          Products
        </AgText>
      </Skeleton>
      <SkeletonDataGrid
        rowCount={4}
        columns={columns}
        initialState={{ density: 'compact' }}
        hideFooter
        enterNewText="Enter New Lot"
        gridKey={`${INVENTORY_TRANSFER_GRID_KEY}.skeleton`}
        storageKey={INVENTORY_TRANSFER_GRID_ID}
        surface="bare"
      />
    </>
  );
};
